import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, User, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import Modal from './Modal';

const API_BASE = process.env.REACT_APP_BACKEND_URL ?? '';

const CallbackForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const name = formData.name.trim();
    const phone = formData.phone.trim();
    if (name.length < 2 || phone.length < 5) {
      setError('Заполните имя и телефон');
      return;
    }
    if (!consent) {
      setError('Подтвердите согласие на обработку персональных данных');
      return;
    }
    setIsSubmitting(true);
    const payload = {
      name,
      phone,
      message: 'Заказ обратного звонка с сайта',
      source: 'перезвонить',
      consent: true,
    };
    const url = `${API_BASE}/api/messages`;

    // Каскад: axios → fetch → sendBeacon. Любой успех → ok.
    let succeeded = false;
    let lastErr = null;

    // 1) axios
    try {
      await axios.post(url, payload);
      succeeded = true;
    } catch (e) {
      lastErr = e;
      console.warn('[CallbackForm] axios failed, trying fetch…', e?.code || e?.message);
    }

    // 2) fetch (иногда обходит AdBlock который трогает axios)
    if (!succeeded) {
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          keepalive: true,
        });
        if (res.ok) succeeded = true;
        else { lastErr = new Error(`HTTP ${res.status}`); }
      } catch (e) {
        lastErr = e;
        console.warn('[CallbackForm] fetch failed, trying sendBeacon…', e?.message);
      }
    }

    // 3) Native HTML form через скрытый iframe — пробивает MetaMask SES lockdown и подобные
    //    (extension'ы не могут вмешиваться в стандартный <form> POST)
    //    Submit с уникальным trace_id → потом дёргаем /api/check-trace чтобы подтвердить доставку.
    if (!succeeded) {
      const traceId = 'cb_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      try {
        // Шаг A: submit формы
        await new Promise((resolve, reject) => {
          const iframeName = 'cb_native_' + traceId;
          const iframe = document.createElement('iframe');
          iframe.name = iframeName;
          iframe.style.display = 'none';
          document.body.appendChild(iframe);

          const form = document.createElement('form');
          form.method = 'POST';
          form.action = `${API_BASE}/api/messages-form`;
          form.target = iframeName;
          form.enctype = 'application/x-www-form-urlencoded';
          form.acceptCharset = 'UTF-8';

          Object.entries({ ...payload, consent: 'true', trace_id: traceId }).forEach(([k, v]) => {
            const i = document.createElement('input');
            i.type = 'hidden';
            i.name = k;
            i.value = String(v);
            form.appendChild(i);
          });
          document.body.appendChild(form);

          let loadCount = 0;
          const cleanup = () => {
            if (form.parentNode) document.body.removeChild(form);
            if (iframe.parentNode) document.body.removeChild(iframe);
          };
          const timer = setTimeout(() => { cleanup(); reject(new Error('Native form timeout')); }, 12000);
          iframe.onload = () => {
            loadCount++;
            // первый onload — пустой iframe; ждём реальный ответ сервера
            if (loadCount < 2) return;
            clearTimeout(timer);
            cleanup();
            resolve(true);
          };
          form.submit();
        });
        succeeded = true;
        console.log('[CallbackForm] native form submit OK', traceId);
      } catch (e) {
        lastErr = e;
        console.warn('[CallbackForm] native form failed:', e?.message);
      }
    }

    if (succeeded) {
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({ name: '', phone: '' });
        setConsent(false);
        onClose();
      }, 2000);
    } else {
      console.error('[CallbackForm] all transports failed:', lastErr);
      let detail = '';
      if (lastErr?.response) detail = ` (HTTP ${lastErr.response.status})`;
      else if (lastErr?.code === 'ERR_NETWORK') detail = ' (нет связи — отключите блокировщик или используйте Инкогнито)';
      else if (lastErr?.message) detail = ` (${lastErr.message})`;
      setError(`Не удалось отправить${detail}. Позвоните: +7 918 436-22-55`);
    }
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Заказать звонок">
      {isSuccess ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-8"
        >
          <div className="w-16 h-16 border border-black flex items-center justify-center mx-auto mb-6 bg-accent">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h4 className="text-2xl font-bold font-sans uppercase mb-2">Заявка отправлена!</h4>
          <p className="font-mono text-muted-foreground">Мы перезвоним вам в ближайшее время</p>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <p className="font-mono text-muted-foreground mb-6">
            Оставьте номер телефона и мы перезвоним вам в течение 15 минут
          </p>
          
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="ВАШЕ ИМЯ"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full pl-12 pr-4 py-4 bg-background border border-black font-mono placeholder:text-muted-foreground/50 focus:outline-none focus:bg-accent/5 transition-colors uppercase"
              required
            />
          </div>
          
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="tel"
              placeholder="+7 (___) ___-__-__"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full pl-12 pr-4 py-4 bg-background border border-black font-mono placeholder:text-muted-foreground/50 focus:outline-none focus:bg-accent/5 transition-colors"
              required
            />
          </div>
          
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 border border-black accent-black cursor-pointer flex-shrink-0"
            />
            <span className="font-mono text-[11px] text-black/70 leading-relaxed">
              Принимаю <a href="/oferta" target="_blank" rel="noopener noreferrer" className="underline font-bold">Договор-оферту</a>, <a href="/rules" target="_blank" rel="noopener noreferrer" className="underline font-bold">Правила</a> и согласен с <a href="/privacy" target="_blank" className="underline">политикой конфиденциальности</a>
            </span>
          </label>

          {error && (
            <p className="font-mono text-sm text-red-600 uppercase tracking-wider">// {error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !consent}
            className="w-full py-4 bg-foreground text-background font-mono uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-accent hover:text-white transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] active:translate-y-0 disabled:opacity-70 disabled:hover:translate-y-0 disabled:shadow-none"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                ОТПРАВКА...
              </>
            ) : (
              <>
                <Phone className="w-5 h-5" />
                ПЕРЕЗВОНИТЕ МНЕ
              </>
            )}
          </button>
          
        </form>
      )}
    </Modal>
  );
};

export default CallbackForm;
